<template>
    <div>
        <div class="contract-data-form-inputs">
            <el-form-item>
                <div class="form-label">
                    {{ translate('client_label') }}
                    <span class="required-asterisk">*</span>
                </div>
                <el-select v-model="data.client_id" style="width: 100%" @change="setClient">
                    <el-option
                        :label="client.name"
                        :value="client.id"
                        v-for="client in clients"
                        :key="client.name"
                    >
                        {{ client.name }}
                    </el-option>
                </el-select>
            </el-form-item>
            <el-form-item>
                <div class="form-label">
                    {{ translate('contract_number_label') }}
                    <span class="required-asterisk">*</span>
                </div>
                <el-input v-model="data.contract_number" />
            </el-form-item>
            <el-form-item>
                <div class="form-label">
                    {{ translate('transaction_volume_label') }} (t)
                    <span class="required-asterisk">*</span>
                </div>
                <el-input
                    v-model="data.volume"
                    @blur="volumeValidate()"
                    @input="onlyNumbers"
                />
            </el-form-item>
            <el-form-item>
                <div class="form-label">
                    {{ translate('cultivation_label') }}
                    <span class="required-asterisk">*</span>
                </div>
                <el-select disabled v-model="data.cultivation_id" style="width: 100%">
                    <el-option
                        :label="cultivation.nome"
                        :value="cultivation.codigo"
                        v-for="cultivation in cultivations"
                        :key="cultivation.codigo"
                    >
                        {{ cultivation.nome }}
                    </el-option>
                </el-select>
            </el-form-item>
            <el-form-item>
                <div class="form-label">
                    {{ translate('agricultural_campaign') }}
                    <span class="required-asterisk">*</span>
                </div>
                <el-select v-model="data.safra" style="width: 100%">
                    <el-option
                        :label="safra.ano"
                        :value="safra.ano"
                        v-for="safra in safrasFiltered"
                        :key="safra.ano"
                    >
                        {{ safra.ano }}
                    </el-option>
                </el-select>
            </el-form-item>
            <el-form-item>
                <div class="form-label">
                    {{ translate('contract_date_label') }}
                    <span class="required-asterisk">*</span>
                </div>
                <el-date-picker
                    v-model="data.contract_date"
                    style="width: 100%"
                    format="dd/MM/yyyy"
                />
            </el-form-item>
            <el-form-item>
                <div class="form-label">
                    {{ translate('department') }}
                    <span class="required-asterisk">*</span>
                </div>
                <el-select v-model="data.department" style="width: 100%" @change="handleDistrictChange">
                    <el-option
                        :label="department.nome"
                        :value="department.id"
                        v-for="department in departments"
                        :key="department.nome"
                    >
                        {{ department.nome }}
                    </el-option>
                </el-select>
            </el-form-item>
            <el-form-item>
                <div class="form-label">
                    {{ translate('district') }}
                    <span class="required-asterisk">*</span>
                </div>
                <el-select v-model="data.district" style="width: 100%">
                    <el-option
                        :label="district.nome"
                        :value="district.id"
                        v-for="district in districts"
                        :key="district.nome"
                    >
                        {{ district.nome }}
                    </el-option>
                </el-select>
            </el-form-item>
        </div>
        <div class="contract-data-form-save">
            <el-form-item>
                <el-button @click="draft" :loading="loading" :disabled="!isFormValid || !!form.id">
                    {{ translateWords('next') }}
                </el-button>
                <el-button
                    type="primary"
                    :disabled="!isFormValid"
                    @click="save"
                    :loading="loading"
                >
                    {{ translateWords('save') }}
                </el-button>
            </el-form-item>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { ErrorHandler } from '@/modules/traceability-contract/utils/error-handler';

export default {
    name: 'ContractDataDefault',
    components: {},
    props: {
        value: {
            type: Object,
            default: () => ({
                contract_number: '',
                volume: '',
                cultivation_id: '',
                district: '',
                department: '',
                delivery_location_id: '',
                client_id: '',
                contract_date: ''
            })
        }
    },
    data() {
        return {
            volume: '',
            volumeMask: {
                decimal: '.',
                thousands: '.',
                prefix: '',
                suffix: '',
                precision: 0,
                masked: false
            },
            loading: false
        };
    },
    async mounted() {
        await this.fetchLocalidades();
        this.fetchCultivations();
        this.fetchSafras();
        this.fetchClientes();
    },
    watch: {
        'data.district': function (value) {
            const department = this.departments.find(
                (department) => department.id === this.data.department
            );
            const district = this.districts.find(
                (district) => district.id === value
            );
            if (!department || !district) return;
            this.data.delivery_location_id = `${district.nome} - ${department.sigla}`;
        },
        '$i18n.locale': function() {
            this.fetchSafras();
        }
    },
    computed: {
        ...mapState('traceabilityContractModule', ['form']),
        ...mapGetters('traceabilityContractModule', {
            cultivations: 'getCultivationTypes',
            clients: 'getClients',
            departments: 'getDepartments',
            districts: 'getDistricts',
            safras: 'getSafras'
        }),
        ...mapGetters('traceabilityContractModule', {
            volumeAdded: 'getVolumeAdded',
            isContractDataValid: 'isContractDataValid'
        }),
        data: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        isFormValid() {
            return this.isContractDataValid;
        },
        safrasFiltered() {
            return this.safras.filter(safra => safra.tipoSafra === 'SAFRA');
        }
    },
    methods: {
        ...mapActions('traceabilityContractModule', [
            'fetchCultivations',
            'fetchSafras',
            'fetchClientes',
            'fetchLocalidades',
            'fetchAvailableBalance',
            'fetchDistritosPorLocalidade',
            'createTraceabilityReport',
            'updateTraceabilityReport'
        ]),
        translate(key) {
            const base = 'contrato_rastreabilidade.messages.contract_data';
            return this.$t(`${base}.${key}`);
        },
        translateWords(key) {
            const base = 'contrato_rastreabilidade.words';
            return this.$t(`${base}.${key}`);
        },
        onlyNumbers() {
            this.data.volume = this.data.volume.replace(/\D/g, '');
        },
        volumeValidate() {
            if (this.volumeAdded > Number(this.data.volume)) {
                this.$notify.error({
                    message: this.$t(
                        'contrato_rastreabilidade.messages.errors.transaction_volume_exceeded_error'
                    )
                });
                this.$nextTick(() => {
                    this.data.volume = this.volumeAdded;
                });
            }
        },
        handleDistrictChange(value) {
            this.data.delivery_location_id = '';
            this.$set(this.data, 'district', '');
            this.fetchDistritosPorLocalidade(value);
        },
        async draft() {
            await this.$messageBox.confirm(
                this.$t('contrato_rastreabilidade.messages.dialog_save_contract.message'),
                this.$t('contrato_rastreabilidade.words.warning'),
                {
                    confirmButtonText: this.$t('contrato_rastreabilidade.words.yes'),
                    cancelButtonText: this.$t('contrato_rastreabilidade.words.cancel'),
                    type: 'warning'
                }
            ).then(async () => {
                try {
                    this.loading = true;
                    this.form.status = 'NAO_ENVIADO';
                    if (this.form.id) {
                        await this.updateTraceabilityReport(this.form);
                    } else {
                        await this.createTraceabilityReport(this.form);
                    }
                    this.$notify.success(this.$t('contrato_rastreabilidade.messages.dialog_save_contract.saved_successfully'));
                } catch (error) {
                    const hasMessage = ErrorHandler.catchError(error, 'contrato_rastreabilidade');
                    if (!hasMessage) {
                        this.$notify.error(this.$t('contrato_rastreabilidade.messages.dialog_save_contract.saved_error'));
                    }
                } finally {
                    this.loading = false;
                }
            }).catch(() => {});
        },
        async save() {
            await this.$messageBox.confirm(
                this.$t('contrato_rastreabilidade.messages.dialog_save_contract.message'),
                this.$t('contrato_rastreabilidade.words.warning'),
                {
                    confirmButtonText: this.$t('contrato_rastreabilidade.words.yes'),
                    cancelButtonText: this.$t('contrato_rastreabilidade.words.cancel'),
                    type: 'warning'
                }
            ).then(async () => {
                try {
                    this.loading = true;
                    this.form.status = 'NAO_ENVIADO';
                    if (this.form.id) {
                        await this.updateTraceabilityReport(this.form);
                    } else {
                        await this.createTraceabilityReport(this.form);
                    }
                    await this.$router.push({
                        name: 'contrato-rastreabilidade'
                    });
                    this.$notify.success(this.$t('contrato_rastreabilidade.messages.dialog_save_contract.saved_successfully'));
                } catch (error) {
                    this.$notify.error(this.$t('contrato_rastreabilidade.messages.dialog_save_contract.saved_error'));
                } finally {
                    this.loading = false;
                }
            }).catch(() => {});
        },
        setClient() {
            this.data.client_name = this.clients.find(
                (client) => client.id === this.data.client_id
            ).name;
        }
    }
};
</script>

<style scoped lang="scss">
.contract-data-form-inputs {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-column-gap: 16px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
}
.contract-data-form-save {
    display: flex;
    justify-content: flex-end;
    margin-top: 15px;
}
.form-label {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
}
.required-asterisk {
    color: red;
    font-weight: bold;
    margin-left: 4px;
}
</style>
